import type { ReactNode } from "react";

import { AlertTriangle, RotateCcw } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { getApiErrorMessage } from "@/shared/lib/http-errors";
import { cn } from "@/shared/lib/utils";

interface ErrorStateProps {
  /** Error de RTK Query (`xxxState.error`) o cualquier `Error`/string. */
  error: unknown;
  /** Fallback para el mensaje si no se puede derivar nada. */
  fallback?: string;
  /** Callback de reintento — típicamente `xxxState.refetch`. */
  onRetry?: () => void;
  /** Título principal — default `No pudimos cargar los datos`. */
  title?: string;
  className?: string;
}

/**
 * Error state unificado para failures de listados y detalles. Un lugar
 * que decide cómo luce "la query falló". Siempre ofrece reintento si
 * se le pasa `onRetry`.
 *
 * Mismo footprint vertical que `EmptyState` → se puede intercambiar sin
 * reflow visible en la tabla.
 */
export function ErrorState({
  error,
  fallback = "Intenta de nuevo en unos segundos.",
  onRetry,
  title = "No pudimos cargar los datos",
  className,
}: ErrorStateProps) {
  const message = getApiErrorMessage(error, fallback);

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-4 px-6 py-14 text-center",
        className,
      )}
      role="alert"
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-destructive/10 text-destructive ring-1 ring-destructive/15">
        <AlertTriangle className="h-6 w-6" />
      </div>
      <div className="max-w-sm space-y-1.5">
        <h3 className="font-display text-[1.1rem] font-medium tracking-display-tight text-foreground">
          {title}
        </h3>
        <p className="text-[13.5px] leading-relaxed text-muted-foreground">{message}</p>
      </div>
      {onRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RotateCcw className="h-3.5 w-3.5" />
          Reintentar
        </Button>
      ) : null}
    </div>
  );
}

interface InlineErrorProps {
  error: unknown;
  fallback?: string;
  children?: ReactNode;
  className?: string;
}

/**
 * Versión inline para errores de mutaciones (create/update/delete) que
 * se muestran dentro de un form o drawer, no como pantalla completa.
 */
export function InlineError({ error, fallback, children, className }: InlineErrorProps) {
  if (!error) return null;
  const message = getApiErrorMessage(error, fallback ?? "Ocurrió un error.");
  return (
    <div
      role="alert"
      aria-live="assertive"
      className={cn(
        "flex animate-fade-in-up items-start gap-3 rounded-xl border border-destructive/25 bg-destructive/[0.06] p-3 text-sm text-destructive",
        className,
      )}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">{message}</p>
        {children ? <p className="mt-1 text-[12.5px] opacity-85">{children}</p> : null}
      </div>
    </div>
  );
}
